import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePerfil } from '../../context/PerfilContext';
import { Aviso, useAvisosAMedias } from '../../context/AvisosAMediasContext';
import { formatoEuros } from '../../utils/amediasUtils';
import { AlarmClock, AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  /** 'finanzas': solo lo que toca a Finanzas (para su Dashboard). Por defecto, todo. */
  solo?: 'finanzas';
}

/**
 * Avisos de A medias para enseñar al entrar: deudas cuyo plazo ya pasó y
 * movimientos de otros pendientes de pasar a Finanzas. Sin avisos no pinta nada.
 */
export const AvisosAMedias: React.FC<Props> = ({ solo }) => {
  const { tieneModulo } = usePerfil();
  const { avisos } = useAvisosAMedias();
  if (!tieneModulo('amedias')) return null;
  const lista = solo === 'finanzas' ? avisos.filter(a => a.tipo !== 'deuda') : avisos;
  if (lista.length === 0) return null;
  return (
    <div className="space-y-2 mb-5">
      {lista.map(a => <FilaAviso key={`${a.tipo}-${a.plan.id}`} aviso={a} />)}
    </div>
  );
};

const FilaAviso: React.FC<{ aviso: Aviso }> = ({ aviso: a }) => {
  const { pasarAFinanzas, posponer } = useAvisosAMedias();
  const [pasando, setPasando] = useState(false);
  const plan = <Link to={`/amedias/${a.plan.id}`} className="font-semibold hover:underline">{a.plan.icono} {a.plan.nombre}</Link>;
  const nombre = (id: string) => a.plan.participantes.find(p => p.id === id)?.nombre ?? 'alguien';

  if (a.tipo === 'errorFinanzas') {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm bg-red-50 dark:bg-red-900/20
                      border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span className="flex-1 min-w-0">{plan}: {a.mensaje}</span>
        <Link to={`/amedias/${a.plan.id}`} className="shrink-0 font-semibold hover:underline">Revisar</Link>
      </div>
    );
  }

  if (a.tipo === 'finanzas') {
    return (
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl text-sm bg-emerald-50 dark:bg-emerald-900/20
                      border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200">
        <RefreshCw className="w-5 h-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="flex-1 min-w-0">
          {plan}: {a.cuantos === 1 ? '1 gasto o pago que apuntó otra persona' : `${a.cuantos} gastos o pagos que apuntaron otros`}, para pasar a Finanzas.
        </span>
        <button disabled={pasando}
          onClick={async () => { setPasando(true); try { await pasarAFinanzas(a.plan.id); } finally { setPasando(false); } }}
          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
          {pasando ? 'Pasando…' : 'Pasar a Finanzas'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl text-sm bg-amber-50 dark:bg-amber-900/20
                    border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-200">
      <AlarmClock className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="flex-1 min-w-0">
        {plan}: te queda pagar{' '}
        {a.deudas.map((d, i) => (
          <span key={d.a}>{i > 0 && (i === a.deudas.length - 1 ? ' y ' : ', ')}
            <span className="font-semibold">{formatoEuros(d.importe)}</span> a {nombre(d.a)}</span>
        ))}
        <span className="opacity-75"> · hace {a.dias} días</span>
      </span>
      <span className="shrink-0 flex items-center gap-2">
        <Link to={`/amedias/${a.plan.id}?pagar=${a.deudas[0].a}`}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white">
          Ya he pagado
        </Link>
        <button onClick={() => posponer(a.plan.id)} className="text-xs font-semibold hover:underline">Más tarde</button>
      </span>
    </div>
  );
};

/** Cuántos avisos tiene A medias, para el contador de su tarjeta en Inicio. */
export const useNumeroAvisosAMedias = () => {
  const { tieneModulo } = usePerfil();
  const { avisos } = useAvisosAMedias();
  return tieneModulo('amedias') ? avisos.length : 0;
};
